import { Component, Inject, OnInit } from "@angular/core"
import { CommonModule } from "@angular/common"
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from "@angular/material/dialog"
import { MatIconModule } from "@angular/material/icon"
import { DataLoaderComponent } from "../../shared/data-loader/data-loader.component"
import {
  ActivityFunnel,
  AdvertiserAnalyticsDetail,
  UserAnalyticsService,
} from "../../services/user-analytics.service"

export interface AdvertiserDetailDialogData {
  userId: number
}

@Component({
  selector: "app-advertiser-detail-dialog",
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatIconModule, DataLoaderComponent],
  templateUrl: "./advertiser-detail-dialog.component.html",
  styleUrl: "./advertiser-detail-dialog.component.css",
})
export class AdvertiserDetailDialogComponent implements OnInit {
  loading = true
  error = ""
  user: AdvertiserAnalyticsDetail | null = null
  activityFunnel: ActivityFunnel | null = null

  constructor(
    private dialogRef: MatDialogRef<AdvertiserDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: AdvertiserDetailDialogData,
    private analytics: UserAnalyticsService,
  ) {}

  ngOnInit(): void {
    this.analytics.getAdvertiserDetail(this.data.userId).subscribe({
      next: ({ user, activityFunnel }) => {
        this.user = user
        this.activityFunnel = activityFunnel
        this.loading = false
      },
      error: () => {
        this.error = "Unable to load advertiser details."
        this.loading = false
      },
    })
  }

  get hasPageActivity(): boolean {
    return this.activityFunnel?.available === true && this.activityFunnel.pages.length > 0
  }

  get activitySourceLabel(): string {
    return this.activityFunnel?.source === "app" ? "in the app" : "via Google Analytics"
  }

  pageLabel(path: string | null | undefined): string {
    const clean = (path || "").split("?")[0].split("#")[0]
    const known: Array<[string, string]> = [
      ["/recordings", "Recordings"],
      ["/audio", "Audio"],
      ["/orders", "Orders"],
      ["/cart", "Cart"],
      ["/payment-success", "Payment success"],
      ["/payment-failure", "Payment failed"],
      ["/payment-return", "Payment return"],
      ["/payment", "Payment"],
      ["/userhome", "Home"],
      ["/production-requests", "Production requests"],
      ["/campaignbuilder", "Campaign builder"],
      ["/campaign-preview", "Campaign preview"],
      ["/managecampaigns", "Campaigns"],
      ["/billing", "Billing"],
      ["/my-offers", "My offers"],
      ["/profile", "Profile"],
    ]
    for (const [prefix, label] of known) {
      if (clean === prefix || clean.startsWith(`${prefix}/`)) return label
    }
    return clean || "—"
  }

  get displayName(): string {
    if (!this.user) return "—"
    const name = `${this.user.firstname || ""} ${this.user.lastname || ""}`.trim()
    return name || "—"
  }

  get initials(): string {
    if (!this.user) return "?"
    const first = (this.user.firstname || "").charAt(0)
    const last = (this.user.lastname || "").charAt(0)
    return (first + last).toUpperCase() || "?"
  }

  accountTypeLabel(type: string | null | undefined): string {
    if (type === "business") return "Business"
    if (type === "individual") return "Individual"
    return type || "—"
  }

  providedText(value: string | null | undefined): string {
    const text = (value || "").trim()
    return text || "Not provided"
  }

  formatDate(value: string | null): string {
    if (!value) return "—"
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })
  }

  formatDateTime(value: string | null): string {
    if (!value) return "Never logged in"
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })
  }

  formatReminderSent(value: string | null): string {
    if (!value) return "Never sent"
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })
  }

  formatMoney(value: number): string {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value)
  }

  close(): void {
    this.dialogRef.close()
  }
}
